import { Font, StyleSheet } from "@react-pdf/renderer";

/**
 * Shared look for every Electrical report document. Ported from
 * Csa/report/pdfTheme.js — same palette, same fonts, same cover frame
 * (shared with HSE and CSA already; see pdfSections.js), so the document
 * cannot drift from its sibling reports by accident.
 */

/* ------------------------------------------------------------------ */
/* fonts                                                               */
/* ------------------------------------------------------------------ */

/**
 * Montserrat, from `public/fonts` — served from our own origin rather than
 * fetched from Google at render time. Browser only: react-pdf's Node build
 * treats the origin-relative `src` as a filesystem path, which is what makes
 * this renderable under test (falling back to Helvetica there).
 */
function registerFonts() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "Helvetica";
  }

  try {
    Font.register({
      family: "Montserrat",
      fonts: [
        { src: "/fonts/Montserrat-Regular.ttf", fontWeight: 400 },
        { src: "/fonts/Montserrat-Light.ttf", fontWeight: 300 },
        { src: "/fonts/Montserrat-Bold.ttf", fontWeight: 700 },
        { src: "/fonts/Montserrat-Italic.ttf", fontWeight: 400, fontStyle: "italic" },
      ],
    });

    // Long observation text in a narrow table cell has to break somewhere.
    // Whole words, not react-pdf's default mid-word hyphenation.
    Font.registerHyphenationCallback((word) => [word]);

    return "Montserrat";
  } catch (e) {
    console.warn("Electrical PDF: could not register Montserrat, using Helvetica —", e.message);
    return "Helvetica";
  }
}

export const FONT = registerFonts();

/* ------------------------------------------------------------------ */
/* palette                                                             */
/* ------------------------------------------------------------------ */

export const C = {
  ink: "#1E2A28",
  slate: "#5C6B68",
  rule: "#DCE3E1",
  sunk: "#F1F4F3",
  white: "#FFFFFF",
  teal: "#307268",
  tealDark: "#245952",
  gold: "#EFC71D",
  cream: "#FFF8DC",

  yes: "#2E7D4F",
  partial: "#B8860B",
  no: "#C0392B",
  na: "#7C8A87",
};

/** Status drives a colour in exactly one place, so both layouts agree. */
export const statusColour = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "YES") return C.yes;
  if (v === "PARTIAL") return C.partial;
  if (v === "NO") return C.no;
  return C.na;
};

/* ------------------------------------------------------------------ */
/* styles                                                             */
/* ------------------------------------------------------------------ */

export const styles = StyleSheet.create({
  page: {
    fontFamily: FONT,
    fontSize: 9,
    color: C.ink,
    paddingTop: 52,
    paddingBottom: 46,
    paddingHorizontal: 34,
    lineHeight: 1.45,
  },

  /* ---- running head and foot ---- */
  runningLogo: { width: 62, height: 16, objectFit: "contain", marginRight: 10 },
  runningHeadLeft: { flexDirection: "row", alignItems: "center" },

  runningHead: {
    position: "absolute",
    top: 20,
    left: 34,
    right: 34,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: C.slate,
    borderBottomWidth: 0.5,
    borderBottomColor: C.rule,
    paddingBottom: 5,
  },
  runningFoot: {
    position: "absolute",
    bottom: 20,
    left: 34,
    right: 34,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: C.slate,
    borderTopWidth: 0.5,
    borderTopColor: C.rule,
    paddingTop: 5,
  },

  /* ---- cover ---- */
  cover: { flexGrow: 1, justifyContent: "center", paddingHorizontal: 20 },
  coverRule: { height: 3, width: 74, backgroundColor: C.gold, marginBottom: 22 },
  coverKicker: {
    fontSize: 9,
    letterSpacing: 2,
    color: C.teal,
    fontWeight: 700,
    marginBottom: 12,
  },
  coverTitle: { fontSize: 27, fontWeight: 700, lineHeight: 1.2, marginBottom: 6 },
  coverSite: { fontSize: 14, color: C.slate, marginBottom: 30 },

  /* ---- the branded cover, matching HSE and CSA ---- */
  coverPage: { position: "relative", padding: 0 },
  coverBackdrop: { position: "absolute", top: 0, left: 0, width: 595.28, height: 841.89 },
  coverImage: { width: 595.28, height: 841.89 },

  coverPanel: {
    position: "absolute",
    left: 30,
    top: 424,
    width: 352,
    height: 176,
    backgroundColor: C.gold,
  },
  coverPanelInner: { paddingTop: 26, paddingLeft: 28 },
  coverDetailRow: { flexDirection: "row", marginBottom: 12 },
  coverDetailLabel: { width: 74, fontSize: 10.5, fontWeight: 700, color: C.tealDark },
  coverDetailValue: { fontSize: 10.5, color: C.tealDark, flex: 1 },

  coverProduct: { position: "absolute", left: 168, top: 676, width: 380 },
  coverProductText: { fontSize: 33, fontWeight: 700, lineHeight: 1.16, color: "#FFFFFF" },
  coverYearRow: { flexDirection: "row", alignItems: "center", marginTop: 10 },
  coverYearLine: { width: 92, height: 2.5, backgroundColor: "#FFFFFF", marginRight: 12 },
  coverYear: { fontSize: 17, fontWeight: 700, color: "#FFFFFF" },

  /* ---- the details page the cover used to carry ---- */
  detailsHead: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  detailsSite: { fontSize: 12, color: C.slate, marginBottom: 22 },

  /* ---- the PARTICULARS / DETAILS table, ported from the old CSA export ---- */
  coverTable: { marginTop: 10, borderWidth: 1, borderColor: C.ink },
  coverTableRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: C.ink },
  coverTableRowLast: { flexDirection: "row" },
  coverTableHeadCell: {
    width: "50%",
    backgroundColor: C.gold,
    padding: 7,
    borderRightWidth: 1,
    borderRightColor: C.ink,
  },
  coverTableHeadText: { fontSize: 10, fontWeight: 700, color: C.ink, textAlign: "center" },
  coverTableCell: { width: "50%", padding: 7, borderRightWidth: 1, borderRightColor: C.ink },
  coverTableCellLast: { borderRightWidth: 0 },
  coverTableCellText: { fontSize: 9.5, color: C.ink },

  /* ---- headings ---- */
  h1: { fontSize: 15, fontWeight: 700, color: C.tealDark, marginBottom: 3 },
  h1Rule: { height: 2, backgroundColor: C.gold, width: 40, marginBottom: 12 },
  h2: { fontSize: 11, fontWeight: 700, marginTop: 14, marginBottom: 5 },
  h3: { fontSize: 9.5, fontWeight: 700, color: C.teal, marginTop: 10, marginBottom: 4 },
  body: { fontSize: 9.5, marginBottom: 7, textAlign: "justify" },
  muted: { fontSize: 8.5, color: C.slate },

  /* ---- score tables ---- */
  scoreHero: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.sunk,
    borderLeftWidth: 3,
    borderLeftColor: C.gold,
    padding: 14,
    marginBottom: 14,
  },
  scoreBig: { fontSize: 34, fontWeight: 700, color: C.tealDark, marginRight: 16 },
  scoreBand: { fontSize: 13, fontWeight: 700 },

  tRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: C.rule },
  tHead: {
    flexDirection: "row",
    backgroundColor: C.tealDark,
    color: C.white,
    borderBottomWidth: 0,
  },
  tHeadCell: { fontSize: 7.5, fontWeight: 700, padding: 6, letterSpacing: 0.4 },
  tCell: { fontSize: 8.5, padding: 6 },
  tCellTight: { fontSize: 8, padding: 5 },
  zebra: { backgroundColor: C.sunk },

  /* ---- the findings register ---- */
  regHead: {
    flexDirection: "row",
    backgroundColor: C.tealDark,
    color: C.white,
  },
  regHeadCell: {
    fontSize: 6.8,
    fontWeight: 700,
    padding: 5,
    letterSpacing: 0.35,
    borderRightWidth: 0.5,
    borderRightColor: "rgba(255,255,255,0.25)",
  },
  regRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: C.rule,
    minPresenceAhead: 40,
  },
  regCell: {
    fontSize: 7.4,
    padding: 5,
    borderRightWidth: 0.5,
    borderRightColor: C.rule,
  },
  statusPill: {
    fontSize: 6.8,
    fontWeight: 700,
    color: C.white,
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 2,
    textAlign: "center",
  },
  thumbRow: { flexDirection: "row", flexWrap: "wrap" },
  thumb: { width: 26, height: 26, marginRight: 2, marginBottom: 2, objectFit: "cover" },

  /* ---- scope ---- */
  scopeItem: {
    borderLeftWidth: 2,
    borderLeftColor: C.gold,
    backgroundColor: C.cream,
    padding: 8,
    marginBottom: 6,
  },
  scopeName: { fontSize: 9, fontWeight: 700, marginBottom: 2 },

  empty: { fontSize: 9, color: C.slate, fontStyle: "italic", marginTop: 6 },
});

/* ------------------------------------------------------------------ */
/* Elaborate layout                                                    */
/* ------------------------------------------------------------------ */

/** The preview watermark — a preview carries no issue number and leaves no trace in the issue trail. */
export const mark = StyleSheet.create({
  wrap: {
    position: "absolute",
    top: "42%",
    left: 0,
    right: 0,
    alignItems: "center",
    transform: "rotate(-30deg)",
  },
  text: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: 46,
    letterSpacing: 3,
    color: "#EFC9C4",
  },
  sub: {
    fontFamily: FONT,
    fontSize: 12,
    letterSpacing: 2,
    color: "#EFC9C4",
    marginTop: 4,
  },
});

export const elab = StyleSheet.create({
  area: {
    fontSize: 13,
    fontWeight: 700,
    color: C.tealDark,
    marginTop: 18,
    marginBottom: 2,
  },
  category: {
    fontSize: 10,
    fontWeight: 700,
    color: C.teal,
    letterSpacing: 0.3,
    marginTop: 12,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: C.rule,
  },

  tbl: {
    borderWidth: 0.6,
    borderColor: C.rule,
    borderStyle: "solid",
    marginBottom: 12,
  },
  tHead: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.sunk,
    borderBottomWidth: 0.6,
    borderBottomColor: C.rule,
    borderBottomStyle: "solid",
    paddingVertical: 4,
    paddingHorizontal: 7,
  },
  tNumber: { fontSize: 9, fontWeight: 700, color: C.tealDark, flex: 1 },
  tStatus: { fontSize: 9, fontWeight: 700 },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 0.6,
    borderBottomColor: C.rule,
    borderBottomStyle: "solid",
  },
  tRowLast: { flexDirection: "row" },
  tLabel: {
    width: 92,
    fontSize: 8,
    fontWeight: 700,
    color: C.slate,
    paddingVertical: 5,
    paddingHorizontal: 7,
    borderRightWidth: 0.6,
    borderRightColor: C.rule,
    borderRightStyle: "solid",
  },
  tValue: { flex: 1, fontSize: 9, lineHeight: 1.5, paddingVertical: 5, paddingHorizontal: 7 },
  tPhotos: { flex: 1, flexDirection: "row", flexWrap: "wrap", paddingVertical: 5, paddingHorizontal: 7 },

  photo: { width: 74, height: 56, marginRight: 5, marginBottom: 5, objectFit: "cover" },
});
